import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { RotateCcw, TriangleAlert } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Shown instead of the default card. Receives the caught error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Deliberately built from raw elements rather than the UI kit: if a crash
 * originates inside a shared primitive, the fallback must not depend on it.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[argus] unhandled render error', error, info.componentStack)
  }

  private readonly reset = (): void => {
    this.setState({ error: null })
  }

  private readonly reload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    const { error } = this.state

    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div role="alert" className="flex min-h-full w-full items-center justify-center bg-canvas p-4">
        <div className="w-full max-w-md rounded-card border border-line bg-surface p-6 shadow-sheet">
          <span className="mb-4 grid h-11 w-11 place-items-center rounded-control bg-bad-500/12 text-bad-600 dark:text-bad-300">
            <TriangleAlert className="h-5 w-5" aria-hidden="true" />
          </span>
          <h1 className="text-base font-semibold tracking-tight text-fg">Something broke</h1>
          <p className="mt-1.5 text-sm text-fg-muted">
            This screen stopped rendering. Your data is safe — nothing was written to WordPress.
          </p>
          <p className="mt-4 max-h-32 overflow-y-auto rounded-control bg-sunken px-3 py-2 font-mono text-xs break-words text-fg-subtle">
            {error.message || 'Unknown error'}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex h-9 items-center gap-1.5 rounded-control bg-accent px-3.5 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Try again
            </button>
            <button
              type="button"
              onClick={this.reload}
              className="inline-flex h-9 items-center rounded-control border border-line-strong bg-surface px-3.5 text-sm font-medium text-fg transition-colors hover:bg-sunken"
            >
              Reload the app
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export { ErrorBoundary }
